// src/components/admin/page-sections/BlogPostsSection.js
import React, { useState, useEffect, useRef } from 'react';
import LazyImage from '../../LazyImage';

const BlogPostsSection = ({ content }) => {
  const {
    title = 'Latest From Our Blog',
    titleColor = '#1f2937', // text-gray-900
    titleSize = 'text-3xl',
    titleAlignment = 'text-center',
    showTitle = true,
    subtitle = '',
    subtitleColor = '#4b5563', // text-gray-600
    postsCount = 3,
    postsPerRow = {
      mobile: 1,
      tablet: 2,
      desktop: 3
    },
    showViewAllButton = true,
    viewAllButtonText = 'View All Posts',
    viewAllButtonLink = '/blog',
    viewAllButtonColor = '#033568',
    viewAllButtonTextColor = '#ffffff',
    cardStyle = 'shadow', // 'shadow', 'outline', or 'flat'
    cardBorderRadius = '0.5rem',
    cardShadowSize = 'shadow-xl',
    cardBgColor = '#ffffff',
    cardHoverEffect = true,
    showCategory = true,
    showDate = true,
    showExcerpt = true,
    showReadMore = true,
    excerptLines = 2,
    readMoreText = 'Read More',
    readMoreColor = '#2563eb', // text-blue-600
    readMoreIcon = true,
    imageAspectRatio = '60%',
    dateFormat = 'MM/DD/YYYY', // MM/DD/YYYY or DD/MM/YYYY
    categoryColor = '#2563eb', // text-blue-600
    titleFontWeight = 'font-bold',
    spaceBetweenCards = '2rem',
    marginTop = '2rem',
    marginBottom = '8rem',
    paddingTop = '1rem',
    paddingBottom = '1rem',
    animationEnabled = true,
    animationDelay = 150, // ms between each card animation
    apiPath = '/api/blog/posts?limit=6&sort=newest&status=published',
    placeholderImage = '/images/placeholder-blog.jpg'
  } = content;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const postsRef = useRef([]);

  // Fetch blog posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(apiPath);
        const data = await response.json();
        setPosts(data.posts || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setLoading(false);
      }
    };

    fetchPosts();
  }, [apiPath]);

  // Setup intersection observer for animations
  useEffect(() => {
    if (!animationEnabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target;
            const index = parseInt(element.dataset.index || 0);
            
            setTimeout(() => {
              element.classList.remove('opacity-0');
              element.classList.add('opacity-100');
            }, index * animationDelay);
            
            observer.unobserve(element);
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe each post card
    postsRef.current.forEach(element => {
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      postsRef.current.forEach(element => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [animationEnabled, animationDelay, posts.length]);

  // Format date based on selected format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return dateFormat === 'MM/DD/YYYY' 
      ? `${month}/${day}/${year}`
      : `${day}/${month}/${year}`;
  };

  // Get grid columns class
  const getGridColumns = () => {
    return `grid-cols-${postsPerRow.mobile} md:grid-cols-${postsPerRow.tablet} lg:grid-cols-${postsPerRow.desktop}`;
  };

  // Get card style classes
  const getCardClasses = () => {
    let classes = `bg-${cardBgColor} rounded-lg overflow-hidden transition-all duration-500`;
    
    if (cardHoverEffect) {
      classes += ' hover:shadow-2xl transform hover:-translate-y-2';
    }
    
    if (cardStyle === 'shadow') {
      classes += ` ${cardShadowSize}`;
    } else if (cardStyle === 'outline') {
      classes += ' border border-gray-200';
    }
    
    return classes;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4" style={{ marginTop, marginBottom, paddingTop, paddingBottom }}>
      {showTitle && (
        <h2 className={`${titleSize} ${titleFontWeight} ${titleAlignment} font-serif mb-16`} style={{ color: titleColor }}>
          {title}
        </h2>
      )}
      
      {subtitle && (
        <p className={`text-center mb-12 max-w-3xl mx-auto`} style={{ color: subtitleColor }}>
          {subtitle}
        </p>
      )}
      
      <div className="relative">
        <div 
          className={`grid ${getGridColumns()} gap-8`}
          style={{ gap: spaceBetweenCards }}
        >
          {posts.slice(0, postsCount).map((post, index) => (
            <div 
              key={post._id}
              className={`${getCardClasses()} opacity-0`}
              style={{ borderRadius: cardBorderRadius }}
              ref={el => postsRef.current[index] = el}
              data-index={index}
            >
              <div 
                onClick={() => window.location.href = `/blog/${post.slug}`}
                className="cursor-pointer"
              >
                <div 
                  className="bg-cover bg-center cursor-pointer"
                  style={{ 
                    backgroundImage: `url(${post.coverImage || placeholderImage})`,
                    paddingBottom: imageAspectRatio
                  }}
                />
                
                <div className="p-6">
                  {(showCategory || showDate) && (
                    <div className="flex items-center mb-2">
                      {showDate && (
                        <span className="text-xs text-gray-500">{formatDate(post.publishedAt)}</span>
                      )}
                      
                      {showCategory && showDate && (
                        <span className="mx-2 text-gray-400">•</span>
                      )}
                      
                      {showCategory && (
                        <span className="text-xs" style={{ color: categoryColor }}>
                          {post.category?.name || 'Uncategorized'}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <h3 className={`text-xl ${titleFontWeight} mb-2 text-gray-900`}>{post.title}</h3>
                  
                  {showExcerpt && (
                    <p className={`text-gray-600 mb-4 line-clamp-${excerptLines}`}>{post.excerpt}</p>
                  )}
                  
                  {showReadMore && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = `/blog/${post.slug}`;
                      }}
                      style={{ 
                        boxShadow: 'none', 
                        border: 'none', 
                        transform: 'none',
                        padding: 0,
                        color: readMoreColor
                      }}
                      className="font-medium flex items-center group"
                    >
                      {readMoreText}
                      {readMoreIcon && (
                        <svg 
                          className="w-4 h-4 ml-1 transform transition-transform group-hover:translate-x-1" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {showViewAllButton && posts.length > 0 && (
        <div className="text-center mt-20">
          <button
            onClick={() => window.location.href = viewAllButtonLink}
            className="px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
            style={{
              backgroundColor: viewAllButtonColor,
              color: viewAllButtonTextColor
            }}
          >
            {viewAllButtonText}
          </button>
        </div>
      )}
    </div>
  );
};

export default BlogPostsSection;